import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { publicDirectoryService } from '../services/dataService';
import dhaLogo from '../assets/img/dha-logo.png';
import styles from './assets/css/PublicFacilityLookupPage.module.css';

const getErrorMessage = (error) => (
  error?.response?.data?.detail
  || error?.message
  || 'Unable to search facilities'
);

const formatStatus = (value) => (
  value
    ? value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
    : 'Unknown'
);

const operationalStatusClass = {
  operational: styles.linked,
  maintenance: styles.notLinked,
  non_operational: styles.statusDanger,
};

const copyText = async (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }

  // Clipboard API is unavailable over plain HTTP; fall back to a hidden textarea.
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  try {
    if (!document.execCommand('copy')) {
      throw new Error('Copy command was rejected');
    }
  } finally {
    document.body.removeChild(textarea);
  }
};

const PublicFacilityLookupPage = () => {
  const [search, setSearch] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const copyResetTimer = useRef(null);

  useEffect(() => () => clearTimeout(copyResetTimer.current), []);

  const handleCopyEquipmentId = async (equipmentId) => {
    try {
      await copyText(equipmentId);
      setCopiedId(equipmentId);
    } catch (err) {
      setCopiedId(null);
      window.prompt('Copy the equipment UUID:', equipmentId);
      return;
    }
    clearTimeout(copyResetTimer.current);
    copyResetTimer.current = setTimeout(() => setCopiedId(null), 2000);
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    const term = search.trim();
    if (term.length < 2) {
      setError('Enter at least two characters of the facility name.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      setResult(await publicDirectoryService.searchFacilityDevices(term));
    } catch (err) {
      setResult(null);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.brand}>
          <img src={dhaLogo} alt="Digital Health Agency" />
          <div>
            <strong>Equipment Gateway</strong>
            <span>Public facility connection directory</span>
          </div>
        </div>
        <Link to="/login" className={styles.loginLink}>Staff login</Link>
      </header>

      <main className={styles.content}>
        <section className={styles.hero}>
          <h1>Find Facility Equipment</h1>
          <p>
            Search by facility name to view registered imaging devices and their
            worklist server connection details.
          </p>
          <form onSubmit={handleSearch} className={styles.searchForm}>
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Enter facility name"
              aria-label="Facility name"
            />
            <button type="submit" disabled={loading}>
              {loading ? 'Searching…' : 'Search'}
            </button>
          </form>
          {error ? <div className={styles.error}>{error}</div> : null}
        </section>

        {result ? (
          <section className={styles.results}>
            <div className={styles.resultsHeader}>
              <h2>Search Results</h2>
              <span>
                {result.total_facilities} facilit
                {result.total_facilities === 1 ? 'y' : 'ies'}
              </span>
            </div>

            {result.facilities.length === 0 ? (
              <div className={styles.empty}>No facilities matched “{result.search}”.</div>
            ) : result.facilities.map((facility) => (
              <article key={facility.facility_id} className={styles.facilityCard}>
                <div className={styles.facilityHeader}>
                  <div>
                    <h3>{facility.facility_name}</h3>
                    <span>{facility.facility_id} · {facility.county}</span>
                    <span className={styles.hmis}>
                      HMIS in use: <strong>{facility.hmis || 'Not specified'}</strong>
                    </span>
                  </div>
                  <strong>
                    {facility.devices.length} device{facility.devices.length === 1 ? '' : 's'}
                  </strong>
                </div>

                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Device</th>
                        <th>Modality</th>
                        <th>Active</th>
                        <th>Operational Status</th>
                        <th>System AE Title</th>
                        <th>Server IP Address</th>
                        <th>Port</th>
                        <th>Linked</th>
                        <th>Linked AE Title</th>
                      </tr>
                    </thead>
                    <tbody>
                      {facility.devices.map((device) => (
                        <tr key={device.equipment_id}>
                          <td>
                            <strong>{device.equipment_name}</strong>
                            <span className={styles.assetId}>{device.asset_id}</span>
                            <span className={styles.equipmentUuid}>
                              <code title="Equipment UUID">{device.equipment_id}</code>
                              <button
                                type="button"
                                className={styles.copyButton}
                                onClick={() => handleCopyEquipmentId(device.equipment_id)}
                                aria-label={`Copy equipment UUID for ${device.equipment_name}`}
                              >
                                {copiedId === device.equipment_id ? 'Copied' : 'Copy'}
                              </button>
                            </span>
                          </td>
                          <td>{device.modality || '—'}</td>
                          <td>
                            <span className={device.is_active ? styles.linked : styles.notLinked}>
                              {device.is_active ? 'Active' : formatStatus(device.lifecycle_state)}
                            </span>
                          </td>
                          <td>
                            <span
                              className={
                                operationalStatusClass[device.operational_status] || styles.statusNeutral
                              }
                            >
                              {formatStatus(device.operational_status)}
                            </span>
                          </td>
                          <td><code>{device.system_ae_title || '—'}</code></td>
                          <td><code>{device.server_ip_address}</code></td>
                          <td>{device.server_port}</td>
                          <td>
                            <span className={device.is_linked ? styles.linked : styles.notLinked}>
                              {device.is_linked ? 'Linked' : 'Not linked'}
                            </span>
                          </td>
                          <td><code>{device.linked_ae_title || '—'}</code></td>
                        </tr>
                      ))}
                      {facility.devices.length === 0 ? (
                        <tr>
                          <td colSpan={9} className={styles.emptyCell}>
                            No equipment registered at this facility.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </article>
            ))}
          </section>
        ) : null}
      </main>
    </div>
  );
};

export default PublicFacilityLookupPage;
